/**
 * Activate panel (Phase 44.6).
 *
 * Final step of the setup flow. Summarises the tenant's drafted state,
 * surfaces TODO + validation warnings, then fires POST /api/tenant/activate.
 *
 * Hard blockers (missing required fields per the Phase 43.1
 * activation-requirements validator) are mirrored client-side so the
 * Activate button can be disabled before the user clicks. Soft blockers
 * (TODO placeholder count, unresolved initiative refs) show as amber
 * warnings but don't block.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSetupStore } from "../../stores/setupStore.js";
import { hasTodo, capabilityStatus } from "./components/catalogueHelpers.js";

function collectBlockers(profile, catalogue) {
  const blockers = [];
  if (!profile) {
    blockers.push("TenantProfile is missing");
  } else {
    for (const f of ["name", "domain", "industry", "description"]) {
      if (!profile?.[f]) blockers.push(`tenantProfile.${f} is required`);
    }
  }
  const pillars = catalogue?.pillars || [];
  if (pillars.length !== 3) {
    blockers.push(`productCatalogue needs exactly 3 pillars (has ${pillars.length})`);
  }
  pillars.forEach((p, i) => {
    const caps = p?.capabilities || [];
    if (!caps.some((c) => c?.name?.trim())) {
      blockers.push(
        `Pillar ${i + 1} (${p?.label || p?.id || "unnamed"}) needs at least one capability with a name`
      );
    }
  });
  const initiatives = catalogue?.strategic_initiatives || [];
  if (initiatives.length === 0) {
    blockers.push("productCatalogue needs at least 1 strategic initiative");
  }
  return blockers;
}

function collectSoftWarnings(profile, catalogue) {
  const warnings = [];
  const pillars = catalogue?.pillars || [];
  let todoCount = 0;
  pillars.forEach((p) => {
    (p?.capabilities || []).forEach((c) => {
      if (capabilityStatus(c) === "todo") todoCount += 1;
      if (!c?.name?.trim()) todoCount += 1; // empty counts as incomplete
    });
  });
  if (todoCount > 0) {
    warnings.push(
      `${todoCount} capability field${todoCount === 1 ? "" : "s"} still has a TODO placeholder. Agents may produce lower-quality output on these until filled.`
    );
  }

  // Unresolved initiative capability references.
  const capNames = new Set();
  pillars.forEach((p) =>
    (p?.capabilities || []).forEach((c) => {
      const t = (c?.name || "").trim();
      if (t) capNames.add(t);
    })
  );
  const unresolved = [];
  (catalogue?.strategic_initiatives || []).forEach((init) => {
    (init?.capabilities_required || []).forEach((name) => {
      const t = (name || "").trim();
      if (t && !capNames.has(t)) unresolved.push(t);
    });
  });
  if (unresolved.length > 0) {
    const sample = unresolved.slice(0, 3).join(", ");
    warnings.push(
      `${unresolved.length} initiative reference${unresolved.length === 1 ? "" : "s"} don't resolve to a defined capability${
        unresolved.length > 3 ? ` (e.g. ${sample}, …)` : ` (${sample})`
      }.`
    );
  }

  // Low research confidence.
  if (profile?.research_metadata?.confidence === "low") {
    warnings.push(
      "Research confidence is low — the bootstrap pipeline had limited signal. Consider regenerating the profile before activating."
    );
  }
  return warnings;
}

function ActivationErrorBlock({ message }) {
  // `message` may be an `Error.message` of the form "409: {json}"; try
  // to unpack the structured activation_requirements_not_met payload
  // the backend returns.
  let parsed = null;
  try {
    const m = /^\d+:\s*(.+)$/s.exec(message);
    if (m) parsed = JSON.parse(m[1]);
  } catch {
    parsed = null;
  }
  if (parsed?.error === "activation_requirements_not_met") {
    return (
      <div className="activate-error" role="alert">
        <strong>Activation blocked by the server:</strong>
        <ul>
          {(parsed.blockers || []).map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="activate-error" role="alert">
      {message}
    </div>
  );
}

export default function ActivatePanel() {
  const navigate = useNavigate();
  const profile = useSetupStore((s) => s.profile);
  const catalogue = useSetupStore((s) => s.catalogue);
  const activate = useSetupStore((s) => s.activate);
  const flushNow = useSetupStore((s) => s.flushNow);
  const error = useSetupStore((s) => s.error);
  const autosaveState = useSetupStore((s) => s.autosaveState);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledgedSoft, setAcknowledgedSoft] = useState(false);

  const blockers = useMemo(
    () => collectBlockers(profile, catalogue),
    [profile, catalogue]
  );
  const warnings = useMemo(
    () => collectSoftWarnings(profile, catalogue),
    [profile, catalogue]
  );

  const pillarCount = catalogue?.pillars?.length || 0;
  const capabilityCount = (catalogue?.pillars || []).reduce(
    (acc, p) => acc + (p?.capabilities || []).length,
    0
  );
  const initiativeCount = catalogue?.strategic_initiatives?.length || 0;
  const todoCount = (catalogue?.pillars || []).reduce((acc, p) => {
    const caps = p?.capabilities || [];
    return (
      acc +
      caps.filter((c) => capabilityStatus(c) === "todo" || !c?.name?.trim()).length +
      (hasTodo(p?.description) ? 1 : 0)
    );
  }, 0);

  const hardBlocked = blockers.length > 0;
  // Soft-block only when there are warnings AND the user hasn't ack'd.
  // We DON'T block on warnings outright — the acknowledgement is a
  // "yes I understand, activate anyway" checkbox.
  const requiresAck = warnings.length > 0 && !acknowledgedSoft;
  const buttonDisabled = hardBlocked || requiresAck || submitting || autosaveState === "saving";

  const handleActivate = async () => {
    setSubmitting(true);
    await flushNow();
    const ok = await activate();
    setSubmitting(false);
    if (ok) navigate("/", { replace: true });
  };

  return (
    <div className="setup-section">
      <h2>Almost there</h2>
      <p className="setup-section__muted">Step 3 of 3 — review and activate.</p>

      {/* Summary card */}
      <div className="activate-summary">
        <div className="activate-summary__row">
          <dt>Tenant</dt>
          <dd>{profile?.name || "—"}</dd>
        </div>
        <div className="activate-summary__row">
          <dt>Industry</dt>
          <dd>{profile?.industry || "—"}</dd>
        </div>
        <div className="activate-summary__row">
          <dt>Catalogue</dt>
          <dd>
            {pillarCount} pillar{pillarCount === 1 ? "" : "s"},{" "}
            {capabilityCount} capabilit{capabilityCount === 1 ? "y" : "ies"},{" "}
            {initiativeCount} initiative{initiativeCount === 1 ? "" : "s"}
          </dd>
        </div>
        <div className="activate-summary__row">
          <dt>Research confidence</dt>
          <dd>
            <span
              className={`research-panel__badge research-panel__badge--${
                profile?.research_metadata?.confidence || "low"
              }`}
            >
              {profile?.research_metadata?.confidence || "unknown"}
            </span>
          </dd>
        </div>
        <div className="activate-summary__row">
          <dt>TODO placeholders</dt>
          <dd>{todoCount}</dd>
        </div>
      </div>

      {/* Hard blockers */}
      {hardBlocked && (
        <div className="activate-banner activate-banner--error">
          <strong>Activation requirements not met.</strong> Fix these before
          activating:
          <ul>
            {blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
          <div className="activate-banner__actions">
            <Link to="/setup/profile" className="btn btn-ghost btn-sm">
              Back to profile
            </Link>
            <Link to="/setup/catalogue" className="btn btn-ghost btn-sm">
              Back to catalogue
            </Link>
          </div>
        </div>
      )}

      {/* Soft warnings */}
      {warnings.length > 0 && !hardBlocked && (
        <div className="activate-banner activate-banner--warning">
          <strong>Your catalogue has some unfinished fields.</strong>
          <ul>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
          <p className="activate-banner__muted">
            You can activate now and come back to edit later — agents will run
            against the current state of the catalogue.
          </p>
          <label className="activate-banner__ack">
            <input
              type="checkbox"
              checked={acknowledgedSoft}
              onChange={(e) => setAcknowledgedSoft(e.target.checked)}
            />
            I understand — activate anyway.
          </label>
        </div>
      )}

      {error && <ActivationErrorBlock message={error} />}

      <div className="setup-footer">
        <Link to="/setup/catalogue" className="btn btn-ghost">
          Back to catalogue
        </Link>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleActivate}
          disabled={buttonDisabled}
          title={
            hardBlocked
              ? "Fix the listed blockers first"
              : requiresAck
                ? "Acknowledge the warnings to continue"
                : undefined
          }
        >
          {submitting
            ? "Activating…"
            : autosaveState === "saving"
              ? "Saving edits…"
              : "Activate tenant"}
        </button>
      </div>
    </div>
  );
}
